import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { ArrowLeft, Save, Clock, MapPin, ToggleLeft, ToggleRight, Car, DollarSign, Currency } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  useRideVisibilitySettings,
  useCreateRideVisibilitySettings,
  useUpdateRideVisibilitySettings,
} from '@/api-services/hooks/useSettings';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";


import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import currencyCodes from "currency-codes";


// (Optional - for symbols like ₹, $, €)
import getSymbolFromCurrency from "currency-symbol-map";

interface SettingsFormData {
  id?: number;
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  pitStopAllowed: boolean;
  maxAllowedPitstops: number;
  pitStopCharges: number | null;
  platformFeePercent: number | null;
  currency: string | null;
  isActive: boolean;
}

const LEVELS = [
  { key: 1, label: 'Level 1', color: 'blue' },
  { key: 2, label: 'Level 2', color: 'green' },
  { key: 3, label: 'Level 3', color: 'purple' },
] as const;

// const CURRENCIES = [
//   { label: 'Indian Rupee (₹)', value: 'INR' },
//   { label: 'US Dollar ($)', value: 'USD' },
//   { label: 'Euro (€)', value: 'EUR' },
//   { label: 'British Pound (£)', value: 'GBP' },
//   { label: 'UAE Dirham (AED)', value: 'AED' },
// ];

const CURRENCIES = currencyCodes.data.map((c) => ({
  label: `${c.currency} (${c.code})`,
  value: c.code,
}));



const DistanceWaitTimeSettingsPage: React.FC = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<SettingsFormData>({
    level1DistanceKm: 0,
    level1WaitTimeMin: 0,
    level2DistanceKm: 0,
    level2WaitTimeMin: 0,
    level3DistanceKm: 0,
    level3WaitTimeMin: 0,
    pitStopAllowed: false,
    maxAllowedPitstops: 0,
    pitStopCharges: null,
    platformFeePercent: null,
    currency: null,
    isActive: true,
  });

  const [open, setOpen] = useState(false);

  const { loading: isLoading } = useRideVisibilitySettings({
    onSuccess: (data) => {
      if (data) {
        setFormData({
          id: data.id,
          level1DistanceKm: data.level1DistanceKm ?? 0,
          level1WaitTimeMin: data.level1WaitTimeMin ?? 0,
          level2DistanceKm: data.level2DistanceKm ?? 0,
          level2WaitTimeMin: data.level2WaitTimeMin ?? 0,
          level3DistanceKm: data.level3DistanceKm ?? 0,
          level3WaitTimeMin: data.level3WaitTimeMin ?? 0,
          pitStopAllowed: data.pitStopAllowed ?? false,
          maxAllowedPitstops: data.maxAllowedPitstops ?? 0,
          pitStopCharges: data.pitStopCharges ?? null,
          platformFeePercent: data.platformFeePercent ?? null,
          currency: data.currency ?? null,
          isActive: data.isActive ?? true,
        });
      }
    },
  });

  const createSettings = useCreateRideVisibilitySettings({
    onSuccess: () => toast.success('Settings saved successfully!'),
    onError: (error) => toast.error(`Failed to save settings: ${error.message}`),
  });

  const updateSettings = useUpdateRideVisibilitySettings({
    onSuccess: () => toast.success('Settings updated successfully!'),
    onError: (error) => toast.error(`Failed to update settings: ${error.message}`),
  });

  const handleInputChange = (field: keyof SettingsFormData, value: number | boolean | null | string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const levels = [
      { dist: formData.level1DistanceKm, wait: formData.level1WaitTimeMin, label: 'Level 1' },
      { dist: formData.level2DistanceKm, wait: formData.level2WaitTimeMin, label: 'Level 2' },
      { dist: formData.level3DistanceKm, wait: formData.level3WaitTimeMin, label: 'Level 3' },
    ];

    for (const { dist, wait, label } of levels) {
      if (dist <= 0) {
        toast.error(`${label}: Distance must be greater than 0`);
        return;
      }
      if (wait <= 0) {
        toast.error(`${label}: Wait time must be greater than 0`);
        return;
      }
    }

    const payload = {
      level1DistanceKm: formData.level1DistanceKm,
      level1WaitTimeMin: formData.level1WaitTimeMin,
      level2DistanceKm: formData.level2DistanceKm,
      level2WaitTimeMin: formData.level2WaitTimeMin,
      level3DistanceKm: formData.level3DistanceKm,
      level3WaitTimeMin: formData.level3WaitTimeMin,
      pitStopAllowed: formData.pitStopAllowed,
      maxAllowedPitstops: formData.pitStopAllowed ? formData.maxAllowedPitstops : 0,
      pitStopCharges: formData.pitStopCharges,
      platformFeePercent: formData.platformFeePercent,
      currency: formData.currency,
      isActive: formData.isActive,
    };

    try {
      if (formData.id) {
        await updateSettings.mutate({ id: formData.id, ...payload });
      } else {
        await createSettings.mutate(payload);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  const levelColors = {
    1: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
    2: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
    3: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/30' },
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Distance & Wait Time Settings</h1>
            <p className="text-muted-foreground mt-1">
              Configure distance and wait time for each search level
            </p>
          </div>
        </div>
        <Badge variant={formData.isActive ? 'default' : 'secondary'} className="text-sm px-3 py-1">
          {formData.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings - 3 Level Cards */}
        <div className="lg:col-span-2 space-y-4">
          {LEVELS.map(({ key, label }) => {
            const colors = levelColors[key];
            const distField = `level${key}DistanceKm` as keyof SettingsFormData;
            const waitField = `level${key}WaitTimeMin` as keyof SettingsFormData;

            return (
              <Card key={key} className={`border ${colors.border}`}>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                      <span className={`text-sm font-bold ${colors.text}`}>{key}</span>
                    </div>
                    <div>
                      <CardTitle className="text-base">{label}</CardTitle>
                      <CardDescription>Distance and wait time for search level {key}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={distField} className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        Distance (km)
                      </Label>
                      <Input
                        id={distField}
                        type="number"
                        min={0}
                        step={0.1}
                        value={formData[distField] as number || ''}
                        onChange={(e) => handleInputChange(distField, parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={waitField} className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        Wait Time (minutes)
                      </Label>
                      <Input
                        id={waitField}
                        type="number"
                        min={0}
                        step={1}
                        value={formData[waitField] as number || ''}
                        onChange={(e) => handleInputChange(waitField, parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          {/* <Card className={formData.isActive ? 'border-green-500/50' : 'border-gray-500/50'}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {formData.isActive ? (
                  <ToggleRight className="w-5 h-5 text-green-500" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                )}
                Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-sm">Enable Settings</span>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                />
              </div>
            </CardContent>
          </Card> */}

          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Level Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {LEVELS.map(({ key, label }) => {
                  const colors = levelColors[key];
                  const dist = formData[`level${key}DistanceKm` as keyof SettingsFormData] as number;
                  const wait = formData[`level${key}WaitTimeMin` as keyof SettingsFormData] as number;
                  return (
                    <div key={key} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
                      <div className="text-xs text-muted-foreground text-right">
                        <div>{dist} km</div>
                        <div>{wait} min</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

           <Card>
            <CardHeader>
              <CardTitle className="text-base">Currency</CardTitle>
            </CardHeader>
<CardContent>
  <div className="space-y-2">
    <Label>Select Currency</Label>

    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          {formData.currency || "Select currency"}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Search currency..." />

          <CommandEmpty>No currency found</CommandEmpty>

          <CommandGroup className="max-h-60 overflow-auto">
            {CURRENCIES.map((curr) => (
              <CommandItem
                key={curr.value}
                value={curr.label}
                onSelect={() => {
                  handleInputChange("currency", curr.value);
                  setOpen(false);
                }}
              >
                {curr.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</CardContent>
          </Card>
        </div>
      </div>

      {/* Pit Stop Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="w-5 h-5" />
            Pit Stop Settings
          </CardTitle>
          <CardDescription>
            Configure pit stop allowance and maximum number of pit stops per ride
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-6 p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="pitStopAllowed"
                  checked={formData.pitStopAllowed}
                  onCheckedChange={(checked) => handleInputChange('pitStopAllowed', checked as boolean)}
                />
                <Label htmlFor="pitStop" className="text-sm cursor-pointer whitespace-nowrap">
                  Enable Pit Stops
                </Label>
              </div>
              {formData.pitStopAllowed && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Max Allowed Pit Stops:</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={formData.maxAllowedPitstops}
                    onChange={(e) => handleInputChange('maxAllowedPitstops', parseInt(e.target.value) || 0)}
                    className="w-20 h-8"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pitStopCharges" className="flex items-center gap-1">
                  Pit Stop Charges ({formData.currency ? formData.currency : ""})
                </Label>
                <Input
                  id="pitStopCharges"
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="Enter pit stop charges"
                  value={formData.pitStopCharges ?? ''}
                  onChange={(e) => handleInputChange('pitStopCharges', e.target.value ? parseFloat(e.target.value) : null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="platformFeePercent" className="flex items-center gap-1">
                  {/* <Percent className="w-3.5 h-3.5" /> */}
                  Platform Fee (%)
                </Label>
                <Input
                  id="platformFeePercent"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="Enter platform fee percent"
                  value={formData.platformFeePercent ?? ''}
                  onChange={(e) => handleInputChange('platformFeePercent', e.target.value ? parseFloat(e.target.value) : null)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/dashboard')}
        >
          Cancel
        </Button>
        {can('Edit Distance & Wait Settings') && (
          <Button
            onClick={handleSave}
            disabled={createSettings.loading || updateSettings.loading}
            className="bg-gradient-primary hover:opacity-90"
          >
            {(createSettings.loading || updateSettings.loading) ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DistanceWaitTimeSettingsPage;
